import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import qs from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { usePlatformFeatureFlag } from '@datassential/platform-ui-lib';

import Grid from '../../../components/Grid/Grid';
import { getQueryString } from '../../../core/api';
import Modal from '../../../components/Modal/Modal';
import { GlobalContext } from '../../../GlobalContext';
import Button from '../../../components/Button/Button';
import Loader from '../../../components/Loader/Loader';
import RoutesEnum from '../../../core/enums/RoutesEnum';
import { IconArrowRight, IconBack } from '../../../icons';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Scrollbar from '../../../components/Scrollbar/Scrollbar';
import NoResults from '../../../components/NoResults/NoResults';
import { ContentRibbon } from '../../../core/models/ContentRibbon';
import { isLetterTyped, isTargetInput } from '../../../core/helpers';
import useSearchResults from '../../../core/apiHooks/useSearchResults';
import { getSlidesPerView, makeEnding } from '../../../core/formatters';

import './GlobalSearch.styles.scss';
import GlobalSearchNewHeader from './GlobalSearchNewHeader';
import RecentSearches from './RecentSearches/RecentSearches';
import AiSearchResults from './AiSearchResults/AiSearchResults';
import useSearchResultsStream from '../../../core/apiHooks/useSearchResultsStream';
import { iPublication } from '../../../core/models/Publication';

export const AI_SEARCH_FEATURE_FLAG = 'ReportPro New AI Search';

const BEST_TILES_COUNT = 3;
const TAKE_DOCS_COUNT = 14;
const TAKE_MACS_COUNT = 14;
const AI_SEARCH_TAKE_DOCS_COUNT = 140;

function isAllowedRoute(locationPath: string) {
    const DISALLOWED_ROUTES: string[] = [RoutesEnum.ContentWatch];
    return !DISALLOWED_ROUTES.includes(locationPath);
}

export default function GlobalSearch() {
    const [value, setValue] = useState('');
    const [newSearchValue, setNewSearchValue] = useState('');
    const [isMacsResultsOpened, setIsMacsResultsOpened] = useState(false);
    const [isOtherResultsOpened, setIsOtherResultsOpened] = useState(false);
    const [orderedDocsResults, setOrderedDocsResults] = useState<iPublication[]>([]);

    const history = useHistory();
    const location = useLocation();
    const isAiSearchEnabled = usePlatformFeatureFlag(AI_SEARCH_FEATURE_FLAG);
    const {
        wWidth,
        shouldOpenSearchOnType,
        isSearchOpened,
        setIsSearchOpened,
        inMyLibrary,
        setInMyLibrary,
    } = useContext(GlobalContext);

    const otherTilesCount = getSlidesPerView(wWidth - 100, 210);
    const macsTilesCount = getSlidesPerView(wWidth - 100, 210);

    const {
        isLoading,
        data: { macsResults, macsResultsCount, docsResults, docsResultsCount },
    } = useSearchResults(
        value,
        inMyLibrary,
        {
            takeDocs: isAiSearchEnabled ? AI_SEARCH_TAKE_DOCS_COUNT : TAKE_DOCS_COUNT,
            takeMacs: isAiSearchEnabled ? AI_SEARCH_TAKE_DOCS_COUNT : TAKE_MACS_COUNT
        }
    );

    const {
        isLoading: isStreamLoading,
        error: streamError,
        htmlContent,
        statusCode,
        isSuccess: isStreamSuccess,
        citations
    } = useSearchResultsStream(value, isAiSearchEnabled);

	const validCitations = useMemo(() => {
		if (isStreamSuccess && citations?.length > 0 && docsResults?.length > 0) {
			return citations
				.map(citation => docsResults.find(doc => doc.id === citation.id))
				.filter(citation => citation !== undefined) as iPublication[];
		}

		return [];
	}, [isStreamSuccess, citations, docsResults]);

    useEffect(() => {
        if (validCitations.length > 0) {
			const docsWithoutCitations = docsResults?.filter(doc => !validCitations?.some(citation => citation?.id === doc?.id)) || [];

			const orderedDocsResults = [...validCitations, ...docsWithoutCitations];

            setOrderedDocsResults(orderedDocsResults);
        }
    }, [isStreamSuccess, docsResults, citations, validCitations]);

    const {
        isLoading: isRestMacsLoading,
        data: { macsResults: restMacsResults }
    } = useSearchResults(
        value,
        inMyLibrary,
        {
            takeMacs: isMacsResultsOpened ? 100 : 0,
            skipMacs: isAiSearchEnabled ? AI_SEARCH_TAKE_DOCS_COUNT : TAKE_DOCS_COUNT
        }
    );

    const {
        isLoading: isRestOtherLoading,
        data: { docsResults: restOtherResults }
    } = useSearchResults(
        value,
        inMyLibrary,
        {
            takeDocs: isOtherResultsOpened ? 100 : 0,
            skipDocs: isAiSearchEnabled ? AI_SEARCH_TAKE_DOCS_COUNT : TAKE_DOCS_COUNT
        }
    );

    const aiSourceResults = useMemo(() => {
        const sourceDocs = orderedDocsResults.slice(0, validCitations.length)

        const sourceDocsWithCitationPages: iPublication[] = sourceDocs.map(doc => {
            const citation = citations.find(citation => citation.id === doc.id);
            return {
                ...doc,
                pages: citation?.pages.map(page => parseInt(page, 10)).sort((a, b) => a - b)
            }
        })
        return sourceDocsWithCitationPages;
    }, [orderedDocsResults, citations, validCitations]);

    const bestResults = docsResults.slice(0, BEST_TILES_COUNT);
    const otherResultsStartIndex = isAiSearchEnabled ? validCitations.length : BEST_TILES_COUNT;
    const otherResultsCount = docsResultsCount - otherResultsStartIndex;
	const shouldUseDefaultDocsResults = !isAiSearchEnabled || (statusCode && statusCode >= 500)
    const allOtherResults = useMemo(
        () => {
            const docs = shouldUseDefaultDocsResults ? docsResults : orderedDocsResults;
            if (!isOtherResultsOpened) {
                return docs.slice(otherResultsStartIndex, otherResultsStartIndex + otherTilesCount);
            }

            return [
                ...docs.slice(otherResultsStartIndex),
                ...restOtherResults,
            ];
        },
        [shouldUseDefaultDocsResults, docsResults, orderedDocsResults, isOtherResultsOpened, otherResultsStartIndex, restOtherResults, otherTilesCount]
    );
    const allMacsResults = useMemo(
        () => {
            if (!isMacsResultsOpened) {
                return macsResults.slice(0, macsTilesCount);
            }

            return [
                ...macsResults,
                ...restMacsResults,
            ];
        },
        [isMacsResultsOpened, macsResults, macsTilesCount, restMacsResults]
    );

    function handleDocumentKeyDown(event: KeyboardEvent) {
        if (!isTargetInput(event) && !isSearchOpened && isAllowedRoute(location.pathname) && isLetterTyped(event)) {
            event.stopPropagation();
            setIsSearchOpened(true);
        }
    }

    const hasResults = docsResultsCount > 0 || macsResultsCount > 0;

    const handleRecentSearchClick = useCallback((search: string) => {
        setNewSearchValue(search);
        setValue(search);
    }, [setValue, setNewSearchValue]);

    useEffect(() => {
        return () => {
            if (isSearchOpened) {
                setTimeout(() => {
                    setValue('');
                }, 300);
            }
        };
    }, [isSearchOpened]);

    useEffect(() => {
        if (!isSearchOpened && shouldOpenSearchOnType) {
            document.addEventListener('keydown', handleDocumentKeyDown, {
                capture: true
            });
        }
        return () => {
            document.removeEventListener('keydown', handleDocumentKeyDown, {
                capture: true
            });
        };
    }, [isSearchOpened, shouldOpenSearchOnType, location]);

    useEffect(() => {
        const handleSearchClick = (event: MouseEvent) => {
            if (isSearchOpened && (event.target as HTMLElement)?.nodeName.toLowerCase() === 'a') {
                const oldSearch = qs.parse(location.search);

                history.replace({
                    search: getQueryString({
                        ...oldSearch,
                        q: value,
                    }),
                })
            }
        };

        document.addEventListener('click', handleSearchClick, { capture: true });
        return () => {
            document.removeEventListener('click', handleSearchClick, { capture: true });
        };
    }, [isSearchOpened, value, location, history]);

    useEffect(() => {
        const parsed = qs.parse(location.search);
        const searchQuery = parsed.q as string;
        if (searchQuery) {
            setIsSearchOpened(true);
            setValue(searchQuery);
            setNewSearchValue(searchQuery);
        }
    }, [location.search]);

    useEffect(() => {
        if (isSearchOpened) {
            setIsSearchOpened(false);
        }
    }, [location.pathname]);

    useEffect(() => {
        setIsMacsResultsOpened(false);
        setIsOtherResultsOpened(false);
    }, [isLoading]);

    const renderBestResults = useMemo(() => {
        if ((isAiSearchEnabled && streamError === null) || bestResults.length === 0) {
            return null;
        }

        return (
            <div className={`GlobalSearch__result ${isMacsResultsOpened || isOtherResultsOpened ? 'collapsed' : ''}`}>
                <Grid
                    columnsNumber={getSlidesPerView(wWidth - 100, 260)}
                    data={new ContentRibbon({
                        clientModel: {
                            id: 1,
                            title: <span className="GlobalSearch__best-results-title">Your best bets</span>,
                            items: bestResults
                        }
                    })}
                />
            </div>
        )
    }, [bestResults, isAiSearchEnabled, isMacsResultsOpened, isOtherResultsOpened, streamError, wWidth]);

    const renderedOtherResults = (
        <div className={`GlobalSearch__result ${isMacsResultsOpened ? 'collapsed' : ''}`}>
            <Grid
                columnsNumber={otherTilesCount}
                data={new ContentRibbon({
                    clientModel: {
                        id: 3,
                        title: (
                            <>
                                {
                                    isAiSearchEnabled
                                        ? 'Other Suggested Titles'
                                        : <>Other titles feature&nbsp;<span className="accent">{value}</span></>
                                }


                                <Button
                                    className="ShowAllButton"
                                    onClick={() => setIsOtherResultsOpened(!isOtherResultsOpened)}
                                >
                                    {isOtherResultsOpened && <IconBack />}
                                    Show {isOtherResultsOpened ? 'less' : 'all'}
                                    {!isOtherResultsOpened && <IconArrowRight />}
                                </Button>
                            </>
                        ),
                        subTitle: `found in ${isAiSearchEnabled && otherResultsCount > 200 ? '200+' : otherResultsCount} ${makeEnding('report', otherResultsCount)}`,
                        items: allOtherResults
                    }
                })}
            />
            {isRestOtherLoading && <Loader />}
        </div>
    );

    const renderedMacsResults = (
        <div className={`GlobalSearch__result ${isOtherResultsOpened ? 'collapsed' : ''}`}>
            <Grid
                columnsNumber={macsTilesCount}
                data={new ContentRibbon({
                    clientModel: {
                        id: 2,
                        title: (
                            <>
                                {isAiSearchEnabled ? 'Related MACs' : 'MACs'}
                                <Button
                                    className="ShowAllButton"
                                    onClick={() => setIsMacsResultsOpened(!isMacsResultsOpened)}
                                >
                                    {isMacsResultsOpened && <IconBack />}
                                    Show {isMacsResultsOpened ? 'less' : 'all'}
                                    {!isMacsResultsOpened && <IconArrowRight />}
                                </Button>
                            </>
                        ),
                        subTitle: `found in ${macsResultsCount} ${makeEnding('report', macsResultsCount)}`,
                        items: allMacsResults
                    }
                })}
            />
            {isRestMacsLoading && <Loader />}
        </div>
    );


    const totalResultsCount = docsResultsCount + macsResultsCount;
    const showMessage = !isLoading && (!bestResults.length && !allOtherResults.length && !allMacsResults.length);

    const renderLoader = useMemo(() => {
        if (!isAiSearchEnabled && isLoading) {
            return <Loader />;
        }

        if (isStreamLoading && !statusCode) {
            return <Loader />;
        }

        return null;
    }, [isAiSearchEnabled, isLoading, isStreamLoading, statusCode]);

    const renderResults = useMemo(() => {
        if (shouldUseDefaultDocsResults) {

            if (showMessage) {
                return <NoResults
                    title={'Sorry, no results for this search'}
                    subTitle={'Please try again, you can search words like pizza, burger, breakfast, etc'}
                    isAbsolute
                />
            }


            if (!isLoading && hasResults) {

                return <>
                    {renderBestResults}
                    {allOtherResults.length > 0 && renderedOtherResults}
                    {allMacsResults.length > 0 && renderedMacsResults}
                </>
            }
        }

        if (statusCode) {
            return <>
                <AiSearchResults
                    htmlContent={htmlContent}
                    sources={aiSourceResults}
                    statusCode={statusCode}
                    isSuccess={isStreamSuccess}
                    isLoading={isStreamLoading}
                />

                {hasResults && htmlContent.length > 0 && !isStreamLoading ?
                    (
                        <>
                            {renderBestResults}
                            {allOtherResults.length > 0 && renderedOtherResults}
                            {allMacsResults.length > 0 && renderedMacsResults}
                        </>
                    )
                    : (
                        <div className="GlobalSearch__results-inner-loader">
                            <Loader />
                        </div>
                    )}
            </>
        }

        return null;

    }, [aiSourceResults, allMacsResults.length, allOtherResults.length, hasResults, htmlContent, isLoading, isStreamLoading, isStreamSuccess, renderBestResults, renderedMacsResults, renderedOtherResults, shouldUseDefaultDocsResults, showMessage, statusCode]);



    return (
        <Modal
            isOpened={isSearchOpened}
            close={() => {
                let search = qs.parse(location.search || '');
                delete search.q;
                history.replace({ search: getQueryString(search) });
                setNewSearchValue('');
                setIsSearchOpened(false);
            }}
            hasInner={false}
            closeOnEsc
            animationName="slide-top"
            className={cn({ 'Modal--ai-search': isAiSearchEnabled })}
        >
            <div className={cn('GlobalSearch', { 'is-ai-search': isAiSearchEnabled })}>
                {isAiSearchEnabled ? (
                    <GlobalSearchNewHeader setValue={setValue} setNewSearchValue={setNewSearchValue} searchValue={newSearchValue} />
                ) : (
                    <div className="GlobalSearch__header">
                        <input
                            className="GlobalSearch__input"
                            placeholder="Start typing here..."
                            autoFocus
                            value={value}
                            onChange={(event) => setValue(event.target.value)}
                        />
                        <div className="GlobalSearch__header-text">
                            <div style={{ opacity: 0.7 }}>
                                Search for Topical Deep Dives, Menu Adoption Cycles, Trendspotting and Quick Reports
                            </div>
                            {totalResultsCount > 0 && <div>found in {totalResultsCount} documents</div>}
                        </div>
                        <div className="GlobalSearch__toggle">
                            <Checkbox
                                color="white"
                                isChecked={inMyLibrary}
                                label="In my library"
                                onChange={() => setInMyLibrary(!inMyLibrary)}
                                isToggle
                            />
                        </div>
                    </div>
                )}
                {value.trim() === ''
                    ? <RecentSearches onSearch={handleRecentSearchClick} />
                    : (
                        <div className="GlobalSearch__results">
                            <Scrollbar>
                                <div className="GlobalSearch__results-inner">
                                    {renderLoader}
                                    {renderResults}
                                </div>
                            </Scrollbar>
                        </div>
                    )
                }
            </div >
        </Modal >
    );
}
